import { Fragment, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';

function label(meeting) {
  return new Date(meeting.startsAt).toLocaleString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Cancelling is where a guest's plans break, so this asks the only question
 * that matters — where do the people go — rather than warning about them.
 */
export default function CancelMeetingModal({ isOpen, meeting, alternatives, onClose, onConfirm }) {
  const options = useMemo(
    () => (alternatives ?? []).filter((m) => m.id !== meeting?.id && !m.cancelledAt),
    [alternatives, meeting],
  );

  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const effectiveTarget = targetId || options[0]?.id || '';
  const guestCount = meeting?.guestCount ?? 0;
  const paidCount = meeting?.paidGuestCount ?? 0;

  if (!meeting) return null;

  const submit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onConfirm({
        reason: reason.trim() || null,
        rollForward: Boolean(effectiveTarget),
        rollForwardToMeetingId: effectiveTarget || null,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all"
                data-testid="cancel-meeting-modal"
              >
                <form onSubmit={submit} className="px-6 py-5">
                  <Dialog.Title className="text-xl font-playfair-sans text-NBNA-primary">
                    Cancel {label(meeting)}
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-600">
                    The QR code at the door will stop registering people for this morning.
                  </p>

                  <div className="mt-4 rounded-md bg-gray-50 p-4 text-sm text-gray-800">
                    {guestCount === 0 ? (
                      <p data-testid="cancel-impact">No guests are registered for this meeting.</p>
                    ) : (
                      <p data-testid="cancel-impact">
                        <strong>{guestCount}</strong> guest{guestCount === 1 ? '' : 's'} registered
                        {paidCount > 0 ? (
                          <>, <strong>{paidCount}</strong> of whom {paidCount === 1 ? 'has' : 'have'} paid the $30 fee</>
                        ) : null}.
                      </p>
                    )}
                  </div>

                  {guestCount > 0 ? (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cancel-target">
                        Move these guests to
                      </label>
                      <select
                        id="cancel-target"
                        data-testid="cancel-target"
                        className="input-field w-full"
                        value={effectiveTarget}
                        onChange={(e) => setTargetId(e.target.value)}
                      >
                        {options.map((m) => (
                          <option key={m.id} value={m.id}>{label(m)}</option>
                        ))}
                        <option value="">Don&apos;t move anyone</option>
                      </select>
                      <p className="mt-2 text-sm text-gray-600">
                        {effectiveTarget
                          ? 'Everyone is emailed the change. Guests who have paid keep their $30 towards the new date, and anyone who can’t make it can pick another morning from a link in that email.'
                          : 'Everyone is emailed that the meeting is cancelled, with a link to choose another morning themselves. Nobody is refunded automatically.'}
                      </p>
                    </div>
                  ) : null}

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cancel-reason">
                      Reason (optional, for your records)
                    </label>
                    <input
                      id="cancel-reason"
                      type="text"
                      className="input-field w-full"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={isSaving}>
                      Keep meeting
                    </button>
                    <button
                      type="submit"
                      className="btn-primary disabled:opacity-40"
                      data-testid="cancel-confirm"
                      disabled={isSaving}
                    >
                      {isSaving ? 'Cancelling…' : 'Cancel meeting'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
